import React, { useState, useEffect, useRef } from "react";
import { Plus, Edit, Trash2, Save, X, Users, Eye, Download, Bold, Italic, List, Underline, Upload, Image as ImageIcon } from "lucide-react";
import axios from "axios";
import ImageUploader from "./ImageUploader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortfolioAdmin = () => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    technologies: "",
    category: "",
    status: "",
    client: "",
    // New project status field
    isActive: true,
    // New website display fields
    showOnWebsite: true,
    displayRank: 1,
    // New project link field
    projectLink: "",
    // Case Study Fields
    problem: "",
    discovery: "",
    ourWork: "",
    outcome: "",
    bannerImage: "",
    logoImage: "",
    cardImageType: "banner",
    detailImageType: "banner",
    // Case Study Images
    problemImages: [],
    discoveryImages: [],
    ourWorkImages: [],
    outcomeImages: [],
    // Testimonial section
    testimonial: "",
    outcomeStats: [],
    // G2 review — project-specific, shown on the case study page
    g2ReviewQuote: "",
    g2ReviewAuthor: "",
    g2ReviewDesignation: "",
    g2ReviewRating: "",
    g2ReviewUrl: "",
    // Which review (if either) to show on this project's case study page — "g2" | "google" | ""
    reviewSource: ""
  });
  const [availableRankings, setAvailableRankings] = useState([1, 2, 3, 4, 5, 6]);
  const [rankingInfo, setRankingInfo] = useState(null);

  useEffect(() => {
    fetchProjects();
    fetchAvailableRankings();
  }, []);

  const fetchAvailableRankings = async () => {
    try {
      const response = await axios.get(`${API}/admin/portfolio/available-rankings`);
      if (response.data.success) {
        setAvailableRankings(response.data.data.available);
        setRankingInfo(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching available rankings:", error);
    }
  };

  const fetchAvailableRankingsForProject = async (projectId) => {
    try {
      const response = await axios.get(`${API}/admin/portfolio/available-rankings/${projectId}`);
      if (response.data.success) {
        setAvailableRankings(response.data.data.available);
        setRankingInfo(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching available rankings for project:", error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/admin/portfolio`);
      if (response.data.success) {
        setProjects(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // If showOnWebsite checkbox changes, refresh available rankings
    if (name === "showOnWebsite") {
      if (editingProject) {
        fetchAvailableRankingsForProject(editingProject.id);
      } else {
        fetchAvailableRankings();
      }
    }
  };

  const handleRichTextChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleOutcomeStatChange = (index, field, value) => {
    const updated = formData.outcomeStats.map((stat, i) =>
      i === index ? { ...stat, [field]: value } : stat
    );
    setFormData({ ...formData, outcomeStats: updated });
  };

  const addOutcomeStat = () => {
    if (formData.outcomeStats.length < 3) {
      setFormData({ ...formData, outcomeStats: [...formData.outcomeStats, { value: "", label: "" }] });
    }
  };

  const removeOutcomeStat = (index) => {
    setFormData({ ...formData, outcomeStats: formData.outcomeStats.filter((_, i) => i !== index) });
  };

  const insertFormatting = (field, format) => {
    const textarea = document.querySelector(`textarea[name="${field}"]`);
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const val = textarea.value;
    const selected = val.substring(start, end);
    let newText = val;
    let newCursor = start;

    if (format === 'bold') {
      const t = selected || 'bold text';
      newText = val.substring(0, start) + `**${t}**` + val.substring(end);
      newCursor = start + 2 + t.length + 2;
    } else if (format === 'italic') {
      const t = selected || 'italic text';
      newText = val.substring(0, start) + `*${t}*` + val.substring(end);
      newCursor = start + 1 + t.length + 1;
    } else if (format === 'underline') {
      const t = selected || 'underlined text';
      newText = val.substring(0, start) + `__${t}__` + val.substring(end);
      newCursor = start + 2 + t.length + 2;
    } else if (format === 'list') {
      const t = selected || 'list item';
      newText = val.substring(0, start) + `\n- ${t}` + val.substring(end);
      newCursor = start + 3 + t.length;
    } else if (format.startsWith('size-')) {
      const px = format.split('-')[1];
      const t = selected || 'text';
      const open = `<span style="font-size:${px}px;color:inherit">`;
      newText = val.substring(0, start) + open + t + `</span>` + val.substring(end);
      newCursor = start + open.length + t.length + 7;
    } else {
      return;
    }

    setFormData(prev => ({ ...prev, [field]: newText }));
    setTimeout(() => { textarea.setSelectionRange(newCursor, newCursor); textarea.focus(); }, 0);
  };

  const handleRichTextKeyDown = (field, e) => {
    if (e.key !== 'Enter') return;
    const textarea = e.target;
    const pos = textarea.selectionStart;
    const val = textarea.value;
    const lineStart = val.lastIndexOf('\n', pos - 1) + 1;
    const currentLine = val.substring(lineStart, pos);
    if (!currentLine.startsWith('- ')) return;
    e.preventDefault();
    if (currentLine === '- ') {
      const newVal = val.substring(0, lineStart) + val.substring(pos);
      setFormData(prev => ({ ...prev, [field]: newVal }));
      setTimeout(() => textarea.setSelectionRange(lineStart, lineStart), 0);
    } else {
      const newVal = val.substring(0, pos) + '\n- ' + val.substring(pos);
      setFormData(prev => ({ ...prev, [field]: newVal }));
      setTimeout(() => { const np = pos + 3; textarea.setSelectionRange(np, np); }, 0);
    }
  };

  const bannerFileRef = useRef(null);
  const logoFileRef = useRef(null);

  const readFileAsDataURL = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => resolve(ev.target.result);
    reader.readAsDataURL(file);
  });

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const dataURL = await readFileAsDataURL(file);
    setFormData(prev => ({ ...prev, bannerImage: dataURL }));
    e.target.value = '';
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const dataURL = await readFileAsDataURL(file);
    setFormData(prev => ({ ...prev, logoImage: dataURL }));
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const projectData = {
      ...formData,
      technologies: formData.technologies.split(",").map(tech => tech.trim()),
      g2ReviewRating: formData.g2ReviewRating === "" ? null : parseFloat(formData.g2ReviewRating)
    };

    try {
      let response;
      if (editingProject) {
        response = await axios.put(`${API}/admin/portfolio/${editingProject.id}`, projectData);
      } else {
        response = await axios.post(`${API}/admin/portfolio`, projectData);
      }

      if (response.data.success) {
        await fetchProjects();
        await fetchAvailableRankings(); // Refresh available rankings
        resetForm();
      }
    } catch (error) {
      console.error("Error saving project:", error);
      if (!error.response) {
        alert("Cannot connect to the server. Make sure the backend is running on port 8000.");
      } else if (error.response.status === 400 && error.response.data?.detail?.includes("already taken")) {
        alert(`Error: ${error.response.data.detail}`);
        if (editingProject) {
          fetchAvailableRankingsForProject(editingProject.id);
        } else {
          fetchAvailableRankings();
        }
      } else {
        alert(`Error saving project: ${error.response.data?.detail || error.response.statusText}`);
      }
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      outcomeStats: project.outcomeStats || [],
      technologies: project.technologies.join(", "),
      category: project.category,
      status: project.status,
      client: project.client || "",
      // Project status field
      isActive: project.isActive !== undefined ? project.isActive : true,
      // Website display fields
      showOnWebsite: project.showOnWebsite !== undefined ? project.showOnWebsite : true,
      displayRank: project.displayRank || 1,
      projectLink: project.projectLink || "",
      // Case Study Fields
      problem: project.problem || "",
      discovery: project.discovery || "",
      ourWork: project.ourWork || "",
      outcome: project.outcome || "",
      bannerImage: project.bannerImage || "",
      logoImage: project.logoImage || "",
      cardImageType: project.cardImageType || "banner",
      detailImageType: project.detailImageType || "banner",
      // Case Study Images
      problemImages: project.problemImages || [],
      discoveryImages: project.discoveryImages || [],
      ourWorkImages: project.ourWorkImages || [],
      outcomeImages: project.outcomeImages || [],
      // Testimonial section
      testimonial: project.testimonial || "",
      // G2 review — project-specific, shown on the case study page
      g2ReviewQuote: project.g2ReviewQuote || "",
      g2ReviewAuthor: project.g2ReviewAuthor || "",
      g2ReviewDesignation: project.g2ReviewDesignation || "",
      g2ReviewRating: project.g2ReviewRating || "",
      g2ReviewUrl: project.g2ReviewUrl || "",
      // Which review (if either) to show on this project's case study page — "g2" | "google" | ""
      reviewSource: project.reviewSource || ""
    });
    setShowForm(true);
    // Fetch available rankings for this specific project
    fetchAvailableRankingsForProject(project.id);
  };

  const handleDelete = async (projectId) => {
    if (deleteConfirm === projectId) {
      try {
        const response = await axios.delete(`${API}/admin/portfolio/${projectId}`);
        if (response.data.success) {
          setDeleteConfirm(null);
          await fetchProjects();
          await fetchAvailableRankings();
        }
      } catch (error) {
        console.error("Error deleting project:", error);
      }
    } else {
      setDeleteConfirm(projectId);
    }
  };

  const handleDuplicate = async (project) => {
    try {
      const response = await axios.post(`${API}/admin/portfolio/${project.id}/duplicate`);
      if (response.data.success) {
        await fetchProjects();
        await fetchAvailableRankings();
      }
    } catch (error) {
      console.error("Error duplicating project:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      technologies: "",
      category: "",
      status: "",
      client: "",
      // New project status field
      isActive: true,
      // New website display fields
      showOnWebsite: true,
      displayRank: 1,
      // New project link field
      projectLink: "",
      // Case Study Fields
      problem: "",
      discovery: "",
      ourWork: "",
      outcome: "",
      bannerImage: "",
      logoImage: "",
      cardImageType: "banner",
      detailImageType: "banner",
      // Case Study Images
      problemImages: [],
      discoveryImages: [],
      ourWorkImages: [],
      outcomeImages: [],
      testimonial: "",
      outcomeStats: [],
      // G2 review — project-specific, shown on the case study page
      g2ReviewQuote: "",
      g2ReviewAuthor: "",
      g2ReviewDesignation: "",
      g2ReviewRating: "",
      g2ReviewUrl: "",
      // Which review (if either) to show on this project's case study page — "g2" | "google" | ""
      reviewSource: ""
    });
    setEditingProject(null);
    setShowForm(false);
    // Refresh available rankings for new projects
    fetchAvailableRankings();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-lg font-light text-gray-600">Loading projects...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 14, color: "#0A0A0A", marginBottom: 4 }}>
          Portfolio
        </h1>
        <p style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#9CA3AF" }}>
          Manage case study projects shown on the website
        </p>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24 }}>
        <button
          onClick={() => setShowForm(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            background: "#2D5C5A",
            border: "none",
            borderRadius: 6,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 500,
            fontSize: 13,
            color: "#fff",
            cursor: "pointer"
          }}
        >
          + Add Project
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-6xl w-full max-h-[90vh] overflow-y-auto" style={{ color: "#374151" }}>
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-light">
                  {editingProject ? "Edit Project" : "Add New Project"}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Basic Information Section */}
                <div className="border-b border-gray-200 pb-8">
                  <h4 className="text-lg font-light mb-6">Basic Information</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Project Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                        placeholder="e.g. IntelliChat Pro"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Category *
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light bg-white"
                      >
                        <option value="">Select category</option>
                        <optgroup label="AI Services">
                          <option value="AI App Development">AI App Development</option>
                          <option value="Machine Learning">Machine Learning</option>
                          <option value="Multi-Agent Systems">Multi-Agent Systems</option>
                          <option value="AI Consulting">AI Consulting</option>
                          <option value="AI Architecture">AI Architecture</option>
                        </optgroup>
                        <optgroup label="Tech Development">
                          <option value="Web Development">Web Development</option>
                          <option value="Mobile Development">Mobile Development</option>
                          <option value="Cloud Infrastructure">Cloud Infrastructure</option>
                          <option value="Backend Development">Backend Development</option>
                          <option value="Database Solutions">Database Solutions</option>
                          <option value="API Development">API Development</option>
                          <option value="Tech Strategy">Tech Strategy</option>
                          <option value="System Architecture">System Architecture</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-light text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light resize-none"
                      placeholder="Brief description of the project..."
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 mt-6">
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Technologies *
                      </label>
                      <input
                        type="text"
                        name="technologies"
                        value={formData.technologies}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                        placeholder="React, Python, MongoDB"
                      />
                      <p className="text-xs text-gray-500 mt-1">Comma separated</p>
                    </div>

                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Status *
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light bg-white"
                      >
                        <option value="">Select status</option>
                        <option value="Completed">Completed</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Planned">Planned</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Client
                      </label>
                      <input
                        type="text"
                        name="client"
                        value={formData.client}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                        placeholder="Client name (optional)"
                      />
                    </div>
                  </div>
                </div>

                {/* Website Display Settings */}
                <div className="border-b border-gray-200 pb-8">
                  <h4 className="text-lg font-light mb-6">Project Settings</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive !== false}
                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                        className="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-black focus:ring-2"
                      />
                      <label className="text-sm font-light text-gray-700">
                        Project is Active
                      </label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        name="showOnWebsite"
                        checked={formData.showOnWebsite}
                        onChange={(e) => setFormData({...formData, showOnWebsite: e.target.checked})}
                        className="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-black focus:ring-2"
                      />
                      <label className="text-sm font-light text-gray-700">
                        Show on website carousel
                      </label>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-light text-gray-700 mb-2">
                      Project Link (Optional)
                    </label>
                    <input
                      type="url"
                      name="projectLink"
                      value={formData.projectLink}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                      placeholder="https://example.com/project"
                    />
                    <p className="text-xs text-gray-500 mt-1">External link to the live project or demo. Clicking the project banner will redirect here.</p>
                  </div>
                </div>

                {/* Display Ranking Settings */}
                <div className="border-b border-gray-200 pb-8">
                  <h4 className="text-lg font-light mb-6">Website Display Settings</h4>
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-2">
                      Display Rank * {formData.showOnWebsite && (
                        <span className="text-xs text-gray-500">
                          ({rankingInfo?.remaining_slots || 0} slots available)
                        </span>
                      )}
                    </label>
                    {formData.showOnWebsite ? (
                      <select
                        name="displayRank"
                        value={formData.displayRank}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light bg-white"
                      >
                        <option value="">Select ranking (1-6)</option>
                        {availableRankings.map(rank => (
                          <option key={rank} value={rank}>
                            Rank {rank} {rank === 1 ? '(Highest Priority)' : rank === 6 ? '(Lowest Priority)' : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="number"
                        name="displayRank"
                        value={formData.displayRank}
                        onChange={handleInputChange}
                        min="1"
                        max="6"
                        className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light bg-gray-100"
                        placeholder="Not applicable when hidden"
                        disabled
                      />
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.showOnWebsite 
                        ? "Rankings 1-6 are unique. Lower numbers appear first (1 = highest priority)."
                        : "Ranking is not used when project is hidden from website."
                      }
                    </p>
                    {rankingInfo?.used && rankingInfo.used.length > 0 && (
                      <p className="text-xs text-orange-600 mt-1">
                        Already taken: {rankingInfo.used.join(', ')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Project Images Section */}
                <div className="border-b border-gray-200 pb-8">
                  <h4 className="text-lg font-light mb-2">Project Images</h4>
                  <p className="text-xs text-gray-500 mb-6">Upload a wide banner and/or a square logo. Then choose which one appears in project cards and on the detail page.</p>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {/* Banner Image */}
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Banner Image <span className="text-gray-400">(wide, 1200×600px)</span>
                      </label>
                      <div className="border-2 border-dashed border-gray-200 rounded-sm p-4 text-center" style={{ minHeight: 140 }}>
                        {formData.bannerImage ? (
                          <div className="space-y-3">
                            <img src={formData.bannerImage} alt="Banner preview" className="max-h-28 mx-auto rounded-sm shadow" style={{ objectFit: "cover", width: "100%" }} />
                            <div className="flex justify-center gap-3">
                              <button type="button" onClick={() => bannerFileRef.current?.click()} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-sm text-xs font-normal hover:bg-gray-200 transition-colors">
                                <Upload size={12} className="inline mr-1" />Change
                              </button>
                              <button type="button" onClick={() => setFormData(prev => ({ ...prev, bannerImage: "" }))} className="bg-red-100 text-red-700 px-3 py-1.5 rounded-sm text-xs font-normal hover:bg-red-200 transition-colors">Remove</button>
                            </div>
                          </div>
                        ) : (
                          <div className="py-4">
                            <ImageIcon size={32} className="mx-auto text-gray-300 mb-3" />
                            <button type="button" onClick={() => bannerFileRef.current?.click()} className="bg-black text-white px-4 py-2 rounded-sm text-xs font-normal hover:bg-gray-900 transition-colors">
                              <Upload size={12} className="inline mr-1" />Upload Banner
                            </button>
                          </div>
                        )}
                      </div>
                      <input ref={bannerFileRef} type="file" accept="image/*" className="hidden" onChange={handleBannerUpload} />
                    </div>

                    {/* Logo Image */}
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Logo Image <span className="text-gray-400">(PNG with transparency works best)</span>
                      </label>
                      <div className="border-2 border-dashed border-gray-200 rounded-sm p-4 text-center" style={{ minHeight: 140 }}>
                        {formData.logoImage ? (
                          <div className="space-y-3">
                            <div style={{ background: "#0A0A0A", borderRadius: 6, padding: "8px 14px", display: "inline-block" }}>
                              <img src={formData.logoImage} alt="Logo preview" style={{ height: 56, width: "auto", objectFit: "contain", maxWidth: 120 }} />
                            </div>
                            <div className="flex justify-center gap-3">
                              <button type="button" onClick={() => logoFileRef.current?.click()} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-sm text-xs font-normal hover:bg-gray-200 transition-colors">
                                <Upload size={12} className="inline mr-1" />Change
                              </button>
                              <button type="button" onClick={() => setFormData(prev => ({ ...prev, logoImage: "" }))} className="bg-red-100 text-red-700 px-3 py-1.5 rounded-sm text-xs font-normal hover:bg-red-200 transition-colors">Remove</button>
                            </div>
                          </div>
                        ) : (
                          <div className="py-4">
                            <ImageIcon size={32} className="mx-auto text-gray-300 mb-3" />
                            <button type="button" onClick={() => logoFileRef.current?.click()} className="bg-black text-white px-4 py-2 rounded-sm text-xs font-normal hover:bg-gray-900 transition-colors">
                              <Upload size={12} className="inline mr-1" />Upload Logo
                            </button>
                          </div>
                        )}
                      </div>
                      <input ref={logoFileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    </div>
                  </div>

                  {/* Display type selectors */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-3">
                        What to show in <strong>project cards</strong> (homepage + all projects)?
                      </label>
                      <div className="flex gap-4">
                        {["banner", "logo"].map(type => (
                          <label key={type} className="flex items-center gap-2 cursor-pointer" style={{ color: "#374151" }}>
                            <input
                              type="radio"
                              name="cardImageType"
                              value={type}
                              checked={formData.cardImageType === type}
                              onChange={() => setFormData(prev => ({ ...prev, cardImageType: type }))}
                              style={{ accentColor: "#2D5C5A" }}
                            />
                            <span className="text-sm font-light capitalize">{type} image</span>
                            {type === "banner" && !formData.bannerImage && <span className="text-xs text-gray-400">(not uploaded)</span>}
                            {type === "logo" && !formData.logoImage && <span className="text-xs text-gray-400">(not uploaded)</span>}
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">If the selected image is not uploaded, a gradient placeholder is shown.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-3">
                        What to show on the <strong>project detail page</strong>?
                      </label>
                      <div className="flex gap-4">
                        {["banner", "logo"].map(type => (
                          <label key={type} className="flex items-center gap-2 cursor-pointer" style={{ color: "#374151" }}>
                            <input
                              type="radio"
                              name="detailImageType"
                              value={type}
                              checked={formData.detailImageType === type}
                              onChange={() => setFormData(prev => ({ ...prev, detailImageType: type }))}
                              style={{ accentColor: "#2D5C5A" }}
                            />
                            <span className="text-sm font-light capitalize">{type} image</span>
                            {type === "banner" && !formData.bannerImage && <span className="text-xs text-gray-400">(not uploaded)</span>}
                            {type === "logo" && !formData.logoImage && <span className="text-xs text-gray-400">(not uploaded)</span>}
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">If the selected image is not uploaded, a gradient placeholder is shown.</p>
                    </div>
                  </div>
                </div>

                {/* Case Study Section */}
                <div>
                  <h4 className="text-lg font-light mb-6">Case Study Content</h4>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Problem/Challenge
                      </label>
                      <div className="border border-gray-200 rounded-sm">
                        <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50" onMouseDown={(e) => e.preventDefault()}>
                          <button type="button" onClick={() => insertFormatting('problem', 'bold')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bold"><Bold size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('problem', 'italic')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Italic"><Italic size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('problem', 'underline')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Underline"><Underline size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('problem', 'list')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bullet list"><List size={15} /></button>
                          <div style={{ width: 1, height: 14, background: '#e5e7eb', margin: '0 2px' }} />
                          <select onChange={(e) => { if (e.target.value) { insertFormatting('problem', e.target.value); e.target.value = ''; } }} onMouseDown={(e) => e.stopPropagation()} className="text-xs text-gray-500 border border-gray-200 rounded px-1 bg-white focus:outline-none" style={{ height: 22 }}>
                            <option value="">Normal</option>
                            <option value="size-10">10px</option>
                            <option value="size-11">11px</option>
                            <option value="size-12">12px</option>
                            <option value="size-13">13px (small)</option>
                            <option value="size-14">14px (default)</option>
                            <option value="size-16">16px</option>
                            <option value="size-18">18px</option>
                            <option value="size-24">24px (large)</option>
                          </select>
                        </div>
                        <textarea
                          name="problem"
                          value={formData.problem}
                          onChange={(e) => handleRichTextChange('problem', e.target.value)}
                          onKeyDown={(e) => handleRichTextKeyDown('problem', e)}
                          rows={6}
                          className="w-full px-4 py-3 focus:outline-none font-light resize-none"
                          placeholder="Describe the problem or challenge this project addresses..."
                        />
                      </div>
                    </div>

                    {/* Problem/Challenge Images */}
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Problem/Challenge Images (Optional)
                      </label>
                      <ImageUploader
                        images={formData.problemImages}
                        onImagesChange={(images) => setFormData({...formData, problemImages: images})}
                        maxImages={8}
                        sectionName="Problem/Challenge"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Discovery
                      </label>
                      <div className="border border-gray-200 rounded-sm">
                        <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50" onMouseDown={(e) => e.preventDefault()}>
                          <button type="button" onClick={() => insertFormatting('discovery', 'bold')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bold"><Bold size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('discovery', 'italic')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Italic"><Italic size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('discovery', 'underline')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Underline"><Underline size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('discovery', 'list')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bullet list"><List size={15} /></button>
                          <div style={{ width: 1, height: 14, background: '#e5e7eb', margin: '0 2px' }} />
                          <select onChange={(e) => { if (e.target.value) { insertFormatting('discovery', e.target.value); e.target.value = ''; } }} onMouseDown={(e) => e.stopPropagation()} className="text-xs text-gray-500 border border-gray-200 rounded px-1 bg-white focus:outline-none" style={{ height: 22 }}>
                            <option value="">Normal</option>
                            <option value="size-10">10px</option>
                            <option value="size-11">11px</option>
                            <option value="size-12">12px</option>
                            <option value="size-13">13px (small)</option>
                            <option value="size-14">14px (default)</option>
                            <option value="size-16">16px</option>
                            <option value="size-18">18px</option>
                            <option value="size-24">24px (large)</option>
                          </select>
                        </div>
                        <textarea
                          name="discovery"
                          value={formData.discovery}
                          onChange={(e) => handleRichTextChange('discovery', e.target.value)}
                          onKeyDown={(e) => handleRichTextKeyDown('discovery', e)}
                          rows={6}
                          className="w-full px-4 py-3 focus:outline-none font-light resize-none"
                          placeholder="Explain the discovery process and research conducted..."
                        />
                      </div>
                    </div>

                    {/* Discovery Images */}
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Discovery Images (Optional)
                      </label>
                      <ImageUploader
                        images={formData.discoveryImages}
                        onImagesChange={(images) => setFormData({...formData, discoveryImages: images})}
                        maxImages={8}
                        sectionName="Discovery"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Our Work/Solution
                      </label>
                      <div className="border border-gray-200 rounded-sm">
                        <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50" onMouseDown={(e) => e.preventDefault()}>
                          <button type="button" onClick={() => insertFormatting('ourWork', 'bold')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bold"><Bold size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('ourWork', 'italic')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Italic"><Italic size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('ourWork', 'underline')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Underline"><Underline size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('ourWork', 'list')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bullet list"><List size={15} /></button>
                          <div style={{ width: 1, height: 14, background: '#e5e7eb', margin: '0 2px' }} />
                          <select onChange={(e) => { if (e.target.value) { insertFormatting('ourWork', e.target.value); e.target.value = ''; } }} onMouseDown={(e) => e.stopPropagation()} className="text-xs text-gray-500 border border-gray-200 rounded px-1 bg-white focus:outline-none" style={{ height: 22 }}>
                            <option value="">Normal</option>
                            <option value="size-10">10px</option>
                            <option value="size-11">11px</option>
                            <option value="size-12">12px</option>
                            <option value="size-13">13px (small)</option>
                            <option value="size-14">14px (default)</option>
                            <option value="size-16">16px</option>
                            <option value="size-18">18px</option>
                            <option value="size-24">24px (large)</option>
                          </select>
                        </div>
                        <textarea
                          name="ourWork"
                          value={formData.ourWork}
                          onChange={(e) => handleRichTextChange('ourWork', e.target.value)}
                          onKeyDown={(e) => handleRichTextKeyDown('ourWork', e)}
                          rows={6}
                          className="w-full px-4 py-3 focus:outline-none font-light resize-none"
                          placeholder="Detail the solution and work performed..."
                        />
                      </div>
                    </div>

                    {/* Our Work/Solution Images */}
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Our Work/Solution Images (Optional)
                      </label>
                      <ImageUploader
                        images={formData.ourWorkImages}
                        onImagesChange={(images) => setFormData({...formData, ourWorkImages: images})}
                        maxImages={8}
                        sectionName="Our Work/Solution"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Outcome/Results
                      </label>
                      <div className="border border-gray-200 rounded-sm">
                        <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50" onMouseDown={(e) => e.preventDefault()}>
                          <button type="button" onClick={() => insertFormatting('outcome', 'bold')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bold"><Bold size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('outcome', 'italic')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Italic"><Italic size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('outcome', 'underline')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Underline"><Underline size={15} /></button>
                          <button type="button" onClick={() => insertFormatting('outcome', 'list')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bullet list"><List size={15} /></button>
                          <div style={{ width: 1, height: 14, background: '#e5e7eb', margin: '0 2px' }} />
                          <select onChange={(e) => { if (e.target.value) { insertFormatting('outcome', e.target.value); e.target.value = ''; } }} onMouseDown={(e) => e.stopPropagation()} className="text-xs text-gray-500 border border-gray-200 rounded px-1 bg-white focus:outline-none" style={{ height: 22 }}>
                            <option value="">Normal</option>
                            <option value="size-10">10px</option>
                            <option value="size-11">11px</option>
                            <option value="size-12">12px</option>
                            <option value="size-13">13px (small)</option>
                            <option value="size-14">14px (default)</option>
                            <option value="size-16">16px</option>
                            <option value="size-18">18px</option>
                            <option value="size-24">24px (large)</option>
                          </select>
                        </div>
                        <textarea
                          name="outcome"
                          value={formData.outcome}
                          onChange={(e) => handleRichTextChange('outcome', e.target.value)}
                          onKeyDown={(e) => handleRichTextKeyDown('outcome', e)}
                          rows={6}
                          className="w-full px-4 py-3 focus:outline-none font-light resize-none"
                          placeholder="Describe the final outcome and results achieved..."
                        />
                      </div>
                    </div>

                    {/* Outcome/Results Images */}
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Outcome/Results Images (Optional)
                      </label>
                      <ImageUploader
                        images={formData.outcomeImages}
                        onImagesChange={(images) => setFormData({...formData, outcomeImages: images})}
                        maxImages={8}
                        sectionName="Outcome/Results"
                      />
                    </div>

                    {/* Testimonial Section */}
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Testimonial (Optional)
                      </label>
                      <div className="relative">
                        <div className="border border-gray-200 rounded-sm focus-within:border-black transition-colors duration-300">
                          <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50" onMouseDown={(e) => e.preventDefault()}>
                            <button type="button" onClick={() => insertFormatting('testimonial', 'bold')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bold"><Bold size={15} /></button>
                            <button type="button" onClick={() => insertFormatting('testimonial', 'italic')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Italic"><Italic size={15} /></button>
                            <button type="button" onClick={() => insertFormatting('testimonial', 'underline')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Underline"><Underline size={15} /></button>
                            <button type="button" onClick={() => insertFormatting('testimonial', 'list')} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bullet list"><List size={15} /></button>
                            <div style={{ width: 1, height: 14, background: '#e5e7eb', margin: '0 2px' }} />
                            <select onMouseDown={(e) => e.stopPropagation()} onChange={(e) => { if (e.target.value) { insertFormatting('testimonial', e.target.value); e.target.value = ''; } }} className="text-xs text-gray-500 border border-gray-200 rounded px-1 bg-white focus:outline-none" style={{ height: 22 }}>
                              <option value="">Size</option>
                              <option value="size-10">10px</option>
                              <option value="size-11">11px</option>
                              <option value="size-12">12px</option>
                              <option value="size-13">13px</option>
                              <option value="size-14">14px</option>
                              <option value="size-16">16px</option>
                              <option value="size-18">18px</option>
                              <option value="size-24">24px</option>
                            </select>
                          </div>
                          <textarea
                            name="testimonial"
                            value={formData.testimonial}
                            onChange={handleInputChange}
                            onKeyDown={(e) => handleRichTextKeyDown('testimonial', e)}
                            rows={4}
                            className="w-full px-4 py-3 focus:outline-none font-light resize-none"
                            placeholder="Client testimonial or feedback about the project..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* G2 Review Section */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-light">G2 Review (Optional)</h4>
                      <div>
                        <label className="block text-sm font-light text-gray-700 mb-2">
                          Quote
                        </label>
                        <textarea
                          name="g2ReviewQuote"
                          value={formData.g2ReviewQuote}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light resize-none"
                          placeholder="Quote copied from this client's G2 review..."
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-light text-gray-700 mb-2">
                            Reviewer Name
                          </label>
                          <input
                            type="text"
                            name="g2ReviewAuthor"
                            value={formData.g2ReviewAuthor}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                            placeholder="Jane Doe"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-light text-gray-700 mb-2">
                            Reviewer Designation
                          </label>
                          <input
                            type="text"
                            name="g2ReviewDesignation"
                            value={formData.g2ReviewDesignation}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                            placeholder="Head of Risk, NovaTech"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-light text-gray-700 mb-2">
                          Rating (1&ndash;5)
                        </label>
                        <input
                          type="number"
                          name="g2ReviewRating"
                          value={formData.g2ReviewRating}
                          onChange={handleInputChange}
                          min="1"
                          max="5"
                          step="0.1"
                          className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                          placeholder="4.9"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-light text-gray-700 mb-2">
                          G2 Review Link
                        </label>
                        <input
                          type="url"
                          name="g2ReviewUrl"
                          value={formData.g2ReviewUrl}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                          placeholder="https://www.g2.com/products/grassurn-technologies/reviews/..."
                        />
                        <p className="text-xs text-gray-500 mt-1">Shown on this project's case study page in place of the generic testimonial.</p>
                      </div>
                    </div>

                    {/* Case Study Review Source Section */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-light">Case Study Review Source</h4>
                      <div>
                        <label className="block text-sm font-light text-gray-700 mb-3">
                          Which review should appear on this project's <strong>case study page</strong>?
                        </label>
                        <div className="flex gap-4">
                          {["g2", "google"].map(source => (
                            <label key={source} className="flex items-center gap-2 cursor-pointer" style={{ color: "#374151" }}>
                              <input
                                type="radio"
                                name="reviewSource"
                                value={source}
                                checked={formData.reviewSource === source}
                                onChange={() => setFormData(prev => ({ ...prev, reviewSource: source }))}
                                style={{ accentColor: "#2D5C5A" }}
                              />
                              <span className="text-sm font-light capitalize">{source === "g2" ? "G2" : "Google"} review</span>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Leave unset to show neither review on the case study page.</p>
                      </div>
                    </div>

                    {/* Outcome Stats Repeater */}
                    <div className="space-y-3">
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <label style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 12, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase" }}>
                          Outcome Stats (up to 3)
                        </label>
                        {formData.outcomeStats.length < 3 && (
                          <button
                            type="button"
                            onClick={addOutcomeStat}
                            style={{
                              padding: "4px 12px",
                              background: "rgba(45,92,90,0.1)",
                              border: "1px solid rgba(45,92,90,0.3)",
                              borderRadius: 4,
                              fontFamily: "Montserrat, sans-serif",
                              fontWeight: 500,
                              fontSize: 12,
                              color: "#2D5C5A",
                              cursor: "pointer"
                            }}
                          >
                            + Add Stat
                          </button>
                        )}
                      </div>
                      {formData.outcomeStats.map((stat, i) => (
                        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input
                            type="text"
                            value={stat.value}
                            onChange={(e) => handleOutcomeStatChange(i, "value", e.target.value)}
                            placeholder='Value (e.g. "3×")'
                            style={{
                              flex: "0 0 100px",
                              padding: "8px 10px",
                              border: "1px solid #E5E7EB",
                              borderRadius: 6,
                              fontFamily: "Montserrat, sans-serif",
                              fontWeight: 300,
                              fontSize: 13,
                              color: "#374151",
                              outline: "none"
                            }}
                          />
                          <input
                            type="text"
                            value={stat.label}
                            onChange={(e) => handleOutcomeStatChange(i, "label", e.target.value)}
                            placeholder='Label (e.g. "Faster processing")'
                            style={{
                              flex: 1,
                              padding: "8px 10px",
                              border: "1px solid #E5E7EB",
                              borderRadius: 6,
                              fontFamily: "Montserrat, sans-serif",
                              fontWeight: 300,
                              fontSize: 13,
                              color: "#374151",
                              outline: "none"
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeOutcomeStat(i)}
                            style={{
                              background: "#FEF2F2",
                              border: "none",
                              borderRadius: 4,
                              padding: "6px 10px",
                              color: "#DC2626",
                              cursor: "pointer",
                              fontSize: 16,
                              lineHeight: 1
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {formData.outcomeStats.length === 0 && (
                        <p style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#9CA3AF" }}>
                          No stats added yet. Click "+ Add Stat" to add up to 3 outcome stats for the Case Study.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="bg-black text-white px-8 py-4 rounded-sm text-sm font-normal hover:bg-gray-900 transition-all duration-300 hover:scale-105 flex items-center gap-2"
                  >
                    <Save size={16} />
                    {editingProject ? "Update Project" : "Create Project"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="border border-gray-300 text-gray-700 px-8 py-4 rounded-sm text-sm font-normal hover:border-black hover:text-black transition-all duration-300 hover:scale-105"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Projects Table */}
      <div style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 10,
        overflow: "hidden"
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F9FAFB" }}>
              <th style={{ padding: "10px 16px", textAlign: "left", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 10, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>Title</th>
              <th style={{ padding: "10px 16px", textAlign: "left", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 10, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>Category</th>
              <th style={{ padding: "10px 16px", textAlign: "left", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 10, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>Rank</th>
              <th style={{ padding: "10px 16px", textAlign: "left", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 10, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>Homepage</th>
              <th style={{ padding: "10px 16px", textAlign: "left", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 10, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>Status</th>
              <th style={{ padding: "10px 16px", textAlign: "left", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 10, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr key={project.id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                <td style={{ padding: "12px 16px", fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#374151" }}>{project.title}</td>
                <td style={{ padding: "12px 16px", fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#374151" }}>{project.category}</td>
                <td style={{ padding: "12px 16px", fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#374151" }}>
                  {project.showOnWebsite ? project.displayRank : "—"}
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontSize: 11,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    background: project.showOnWebsite ? "#D1FAE5" : "#FEF3C7",
                    color: project.showOnWebsite ? "#065F46" : "#92400E"
                  }}>
                    {project.showOnWebsite ? "Shown" : "Hidden"}
                  </span>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontSize: 11,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    background: project.isActive ? "#EFF6FF" : "#F3F4F6",
                    color: project.isActive ? "#1D4ED8" : "#6B7280"
                  }}>
                    {project.isActive ? "Active" : "Paused"}
                  </span>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => handleEdit(project)}
                      style={{
                        padding: "5px 12px",
                        background: "rgba(45,92,90,0.1)",
                        border: "none",
                        borderRadius: 4,
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 500,
                        fontSize: 12,
                        color: "#2D5C5A",
                        cursor: "pointer"
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDuplicate(project)}
                      style={{
                        padding: "5px 12px",
                        background: "rgba(59,130,246,0.08)",
                        border: "none",
                        borderRadius: 4,
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 500,
                        fontSize: 12,
                        color: "#2563EB",
                        cursor: "pointer"
                      }}
                    >
                      Duplicate
                    </button>
                    <button
                      onClick={() => handleDelete(project.id)}
                      onBlur={() => setDeleteConfirm(null)}
                      style={{
                        padding: "5px 12px",
                        background: deleteConfirm === project.id ? "#DC2626" : "#FEF2F2",
                        border: "none",
                        borderRadius: 4,
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 500,
                        fontSize: 12,
                        color: deleteConfirm === project.id ? "#fff" : "#DC2626",
                        cursor: "pointer"
                      }}
                    >
                      {deleteConfirm === project.id ? "Confirm?" : "Delete"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {projects.length === 0 && (
          <div style={{ padding: "48px 16px", textAlign: "center" }}>
            <p style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 13, color: "#9CA3AF", marginBottom: 16 }}>
              No projects yet. Click "+ Add Project" to create your first case study.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioAdmin;